/**
 * Плашка режима выделения «пачкой» вместо поля ответа «Чатов»: что сделать с отмеченными
 * сообщениями. «Переслать в салон» — все одним запросом в группу «Общее <город>» (окно то же, что
 * у одного сообщения); «Скопировать» — перепиской «время Клиент: текст»; «Замечание» (право
 * `qc_remark`) — те же отметки в поле замечания. Выйти — крестик или Esc.
 * Выделение — `QcSelection.tsx` (`purpose: "pick"`).
 */
import { ClipboardCheck, Copy, Forward, ListChecks, X } from "lucide-react";
import { useEffect, useState } from "react";

import { ForwardDialog } from "@/components/chat/Forward";
import { useQc } from "@/components/qc/QcSelection";
import type { ChatMessage } from "@/lib/clientChats";
import { plural } from "@/lib/plural";

export function PickBar({ messages }: { messages: ChatMessage[] }) {
  const qc = useQc();
  const [copied, setCopied] = useState(false);
  // Открыто окно «Переслать в салон»: Esc закрывает его, а выделение остаётся.
  const [forward, setForward] = useState<{ chatId: number; ids: number[] } | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !forward && qc?.stop();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [qc, forward]);
  if (!qc) return null;
  const ids = new Set([...qc.picked.values()].filter((r) => r.kind === "message").map((r) => r.id));
  // По порядку ленты, а не отметок.
  const list = messages.filter((m) => ids.has(m.id));
  const n = list.length;
  // Общая лента клиента: сообщения из разных его чатов одним запросом не уходят.
  const chats = new Set(list.map((m) => m.chat_id));
  const copy = async () => {
    const text = list
      .map((m) => `${m.date.slice(11, 16)} ${m.out ? "Мы" : "Клиент"}: ${m.text || (m.file ? "‹вложение›" : "")}`)
      .join("\n");
    await navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  const btn =
    "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[14px] font-medium transition-colors disabled:opacity-40";
  return (
    <div
      className="rounded-[18px] bg-[var(--tg-surface)] px-3 py-2.5 shadow-[0_1px_8px_1px_rgba(0,0,0,.12)]"
      data-annot="плашка выделенных сообщений"
    >
      <div className="mb-2 flex items-center gap-2 text-[13px]">
        <span className="min-w-0 font-medium" aria-live="polite">
          {n ? `Выбрано ${n} ${plural(n, "сообщение", "сообщения", "сообщений")}` : "Отметьте сообщения кружками слева"}
        </span>
        <button
          type="button"
          onClick={() => qc.stop()}
          title="Выйти из выделения (Esc)"
          className="ml-auto grid size-7 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
        >
          <X size={17} />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5" data-annot="действия с выделенными">
        <button
          type="button"
          disabled={!n || chats.size > 1}
          onClick={() => setForward({ chatId: [...chats][0]!, ids: list.map((m) => m.id) })}
          title={
            chats.size > 1
              ? "Отмечены сообщения из разных чатов клиента — переслать можно сообщения одного чата"
              : "Все отмеченные — одним запросом в группу салона: фото альбомом, тексты перепиской, номер клиента и отметка стилиста"
          }
          className={`${btn} bg-[var(--tg-primary)] text-white hover:opacity-90`}
          data-annot="переслать выделенные в салон"
        >
          <Forward size={16} /> Переслать в салон
        </button>
        <button
          type="button"
          disabled={!n}
          onClick={() => void copy()}
          title="Скопировать текст отмеченных сообщений перепиской"
          className={`${btn} bg-[var(--tg-hover)] text-[var(--tg-text)] hover:bg-[var(--tg-primary)]/15`}
          data-annot="скопировать выделенные"
        >
          <Copy size={16} /> {copied ? "Скопировано" : "Скопировать"}
        </button>
        {qc.add && (
          <button
            type="button"
            onClick={() => qc.start()}
            title="Замечание контроля качества к отмеченным сообщениям"
            className={`${btn} bg-[var(--tg-hover)] text-[var(--tg-text)] hover:bg-[var(--tg-primary)]/15`}
            data-annot="замечание к выделенным"
          >
            <ClipboardCheck size={16} /> Замечание
          </button>
        )}
      </div>
      {forward && (
        <ForwardDialog chatId={forward.chatId} messageIds={forward.ids} onClose={() => setForward(null)} onSent={() => qc.stop()} />
      )}
    </div>
  );
}

/** Значок в шапке чата: включить и выключить выделение пачкой. */
export function PickHeaderButton() {
  const qc = useQc();
  if (!qc?.pickable) return null;
  const on = qc.active && qc.purpose === "pick";
  return (
    <button
      type="button"
      onClick={() => (on ? qc.stop() : qc.pick())}
      disabled={qc.busy}
      aria-pressed={on}
      title={on ? "Выйти из выделения (Esc)" : "Выбрать несколько сообщений — переслать в салон пачкой, скопировать"}
      className={`grid size-10 flex-none place-items-center rounded-full hover:bg-[var(--tg-hover)] disabled:opacity-40 ${on ? "text-[var(--tg-primary)]" : "text-[var(--tg-sec)]"}`}
      data-annot="выбрать сообщения"
    >
      <ListChecks size={22} />
    </button>
  );
}
